import { API_URL } from './utils'

interface MediaSize {
  url: string
  width?: number
  height?: number
}

export interface Media {
  id: string
  url: string
  alt?: string
  caption?: string
  mimeType?: string
  width?: number
  height?: number
  sizes?: {
    thumbnail?: MediaSize
    card?: MediaSize
    hero?: MediaSize
  }
}

export interface User {
  id: string
  name: string
  bio?: string
  avatar?: Media
  socials?: { url: string }[]
}

export interface Category {
  id: string
  name: string
  slug: string
}

export interface Post {
  id: string
  title: string
  slug: string
  author: User | string
  publishedAt?: string
  coverImage?: Media
  excerpt?: string
  featured?: boolean
  content: unknown // Lexical JSON
  tags?: { tag: string }[]
  primaryCategory?: Category | string
  categories?: (Category | string)[]
  contentModifiedAt?: string
  seo?: {
    metaTitle?: string
    metaDescription?: string
    useExcerptAsDescription?: boolean
    index?: boolean
    follow?: boolean
    canonicalMode?: 'auto' | 'manual'
    canonicalUrl?: string
    ogTitle?: string
    ogDescription?: string
    ogImage?: Media
    twitterCard?: string
    twitterTitle?: string
    twitterDescription?: string
    twitterImage?: Media
  }
  createdAt: string
  updatedAt: string
}

interface PaginatedResponse<T> {
  docs: T[]
  totalDocs: number
  page: number
  totalPages: number
  hasNextPage: boolean
  hasPrevPage: boolean
}

async function get<T>(path: string): Promise<T> {
  const res = await fetch(`${API_URL}/api${path}`, {
    headers: { 'Content-Type': 'application/json' },
  })
  if (!res.ok) throw new Error(`API ${res.status}: ${path}`)
  return res.json()
}

export const postsApi = {
  list: (limit = 20) =>
    get<PaginatedResponse<Post>>(
      `/posts?limit=${limit}&sort=-publishedAt&depth=2`
    ),

  bySlug: async (slug: string): Promise<Post | null> => {
    const res = await get<PaginatedResponse<Post>>(
      `/posts?where[slug][equals]=${encodeURIComponent(slug)}&depth=2&limit=1`
    )
    return res.docs[0] ?? null
  },

  featured: async (): Promise<Post | null> => {
    const res = await get<PaginatedResponse<Post>>(
      `/posts?where[featured][equals]=true&depth=2&limit=1&sort=-publishedAt`
    )
    return res.docs[0] ?? null
  },
}

export function mediaUrl(media?: Media, size: 'thumbnail' | 'card' | 'hero' = 'card') {
  if (!media) return ''
  const sized = media.sizes?.[size]?.url ?? media.url
  if (!sized) return ''
  if (sized.startsWith('http')) {
    try {
      return `${API_URL}${new URL(sized).pathname}`
    } catch {
      return sized
    }
  }
  return `${API_URL}${sized}`
}

export interface ContactMessage {
  first: string
  last: string
  phone: string
  email: string
  company: string
  source: string
  message: string
  consent: boolean
  website: string
}

export const contactApi = {
  send: async (data: ContactMessage): Promise<void> => {
    const res = await fetch(`${API_URL}/api/contact`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    })
    if (!res.ok) {
      const body = await res.json().catch(() => ({}))
      throw new Error(body.error || 'Something went wrong. Please call us at (704) 459-8642.')
    }
  },
}
